import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../../../UI/Btn'
import SettingHint from '../SettingHint'
import CourseCatalog from './CourseCatalog'

import { COMPANY_TYPE_KEYS, COMPANY_TYPE_LABELS, LEARNING_COURSES_DEFAULTS } from '../../../../../../shared/courses'

import styles from '../style.module.scss'
// Derselbe Schalter wie bei den übrigen Voraussetzungen.
import config from '../RequirementsConfig/style.module.scss'
import own from './style.module.scss'

// Ohne Übersetzung (Tests): nur die Platzhalter einsetzen.
const einsetzen = (text, werte = {}) => text.replace(/{{(\w+)}}/g, (_, name) => werte[name] ?? '')

/** Gespeicherter Wert → Formular. Fehlt etwas, gilt die Voreinstellung. */
export const fromOption = value => {
	const wert = value || {}
	const zahl = (quelle, typ, vorgabe) => {
		const x = quelle?.[typ]
		if (x === null) return ''
		return x === undefined ? String(vorgabe ?? '') : String(x)
	}

	return {
		enabled: wert.enabled === true,
		creditEnabled: wert.creditEnabled === true,
		perCompletion: Object.fromEntries(
			COMPANY_TYPE_KEYS.map(typ => [
				typ,
				zahl(wert.perCompletion, typ, LEARNING_COURSES_DEFAULTS.perCompletion[typ])
			])
		),
		cap: Object.fromEntries(
			COMPANY_TYPE_KEYS.map(typ => [typ, zahl(wert.cap, typ, LEARNING_COURSES_DEFAULTS.cap[typ])])
		),
		reminderDays: String(wert.reminderDays ?? LEARNING_COURSES_DEFAULTS.reminderDays)
	}
}

/**
 * Formular → gespeicherter Wert. Liefert `{ value }` oder `{ error }`.
 *
 * Ein leeres Feld bei der Obergrenze heißt „keine Obergrenze" (`null`). Beim
 * Betrag pro Abschluss ist leer ein Fehler — `Number("")` wäre 0, und die
 * Gutschrift wäre still abgeschaltet.
 */
export const toOption = (form, t = einsetzen) => {
	const perCompletion = {}
	const cap = {}

	for (const typ of COMPANY_TYPE_KEYS) {
		const betrag = String(form.perCompletion[typ] ?? '')
			.replace(',', '.')
			.trim()
		if (betrag === '' || !Number.isFinite(Number(betrag)) || Number(betrag) < 0 || Number(betrag) > 1000) {
			return { error: t('Amount per completion for {{type}}: 0 to 1000 €.', { type: typ }) }
		}
		perCompletion[typ] = Math.round(Number(betrag) * 100) / 100

		const grenze = String(form.cap[typ] ?? '')
			.replace(',', '.')
			.trim()
		if (grenze === '') cap[typ] = null
		else if (!Number.isFinite(Number(grenze)) || Number(grenze) < 0 || Number(grenze) > 100000) {
			return { error: t('Cap for {{type}}: empty (no cap) or 0 to 100000 €.', { type: typ }) }
		} else cap[typ] = Math.round(Number(grenze) * 100) / 100
	}

	const tage = Number(form.reminderDays)
	if (!Number.isInteger(tage) || tage < 0 || tage > 90) {
		return { error: t('Reminder: 0 to 90 days (0 = no reminder).') }
	}

	return {
		value: {
			enabled: Boolean(form.enabled),
			// Ohne Kurse keine Gutschrift — sonst stünde eine eingeschaltete
			// Gutschrift auf etwas, das niemand erwerben kann.
			creditEnabled: Boolean(form.enabled) && Boolean(form.creditEnabled),
			perCompletion,
			cap,
			reminderDays: tage
		}
	}
}

/**
 * Kurse & Gutschrift.
 *
 * Zwei Schalter: Ob es Kurse überhaupt gibt, und ob ein Abschluss Geld wert
 * ist. Der Kurs kann ohne Gutschrift laufen (etwa im ersten Jahr zum
 * Ausprobieren); eine Gutschrift ohne Kurs gibt es nicht.
 *
 * Für welche Unternehmensformen ein Kurs gilt, steht am Kurs selbst
 * (Kursverwaltung). Hier steht, was ein Abschluss je Unternehmensform bringt.
 */
export default function LearningCourses(props) {
	const { t } = useTranslation()
	const ausgang = useMemo(() => fromOption(props.form?.learningCourses), [props.form])
	const [form, setForm] = useState(ausgang)
	const [speichert, setSpeichert] = useState(false)

	useEffect(() => setForm(ausgang), [ausgang])

	const geaendert = JSON.stringify(form) !== JSON.stringify(ausgang)

	const setze = (feld, wert) => setForm(vorher => ({ ...vorher, [feld]: wert }))
	const setzeTyp = (feld, typ, wert) => setForm(vorher => ({ ...vorher, [feld]: { ...vorher[feld], [typ]: wert } }))

	const speichern = event => {
		event.preventDefault()
		const { value, error } = toOption(form, t)
		if (error) return toast.error(error)

		setSpeichert(true)
		props
			.updateOptions({ name: 'learningCourses', value })
			.then(() => toast.success(t('Options was updated')))
			.finally(() => setSpeichert(false))
		return undefined
	}

	return (
		<form id="learningCourses" className={styles.root} onSubmit={speichern}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Courses & credit')}</h3>
				<SettingHint
					example={t(
						'Example: courses on, credit on, 10 € per completion, at most 100 € per Junior Company.'
					)}
					warning={t(
						'The credit is deducted from the last invoice of the school year (JC: annual report, JCC/JBC: the only invoice). What goes beyond the invoice stays as credit and is not paid out. Completions after the last report has been submitted no longer count.'
					)}
				>
					{t(
						'Students complete courses with videos and quizzes in the app and receive a certificate. Each completion with certificate can be credited to the Junior Company. Which courses exist and for which company types they apply is set under Course management.'
					)}
				</SettingHint>

				<div className={styles.sectionContent}>
					<label className={clsx(config.switch, styles.field)}>
						<input
							type="checkbox"
							checked={form.enabled}
							onChange={e => setze('enabled', e.target.checked)}
						/>
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>
							{form.enabled
								? t('Course area switched on (menu item “Courses” for students and teachers)')
								: t('switched off')}
						</span>
					</label>
					{!form.enabled && (
						<p className={styles.hintOff}>
							{t(
								'Switched off. Students and teachers see no courses; certificates already issued remain valid.'
							)}
						</p>
					)}

					<CourseCatalog concept={props.form?.conceptRequirement} enabled={ausgang.enabled} />

					<h4 className={styles.subTitle}>{t('Credit on the invoice')}</h4>
					<label className={clsx(config.switch, styles.field)}>
						<input
							type="checkbox"
							checked={form.enabled && form.creditEnabled}
							disabled={!form.enabled}
							onChange={e => setze('creditEnabled', e.target.checked)}
						/>
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>
							{form.enabled && form.creditEnabled ? t('Credit switched on') : t('switched off')}
						</span>
					</label>
					<p className={styles.subHint}>
						{t(
							'Credits already earned remain if the credit is switched off; new completions no longer count from then on. A change of amount applies to invoices created afterwards.'
						)}
					</p>

					<div className={clsx(styles.flexContainer, styles.fieldsEven)}>
						{COMPANY_TYPE_KEYS.map(typ => (
							<div key={typ} className={own.spalte}>
								<b>{COMPANY_TYPE_LABELS[typ]}</b>
								<label className={own.feld}>
									{t('Per completion (€)')}
									<input
										type="text"
										inputMode="decimal"
										value={form.perCompletion[typ]}
										disabled={!form.enabled || !form.creditEnabled}
										onChange={e => setzeTyp('perCompletion', typ, e.target.value)}
										aria-label={`${t('Per completion (€)')} ${typ}`}
									/>
								</label>
								<label className={own.feld}>
									{t('Cap per Junior Company (€, empty = none)')}
									<input
										type="text"
										inputMode="decimal"
										value={form.cap[typ]}
										disabled={!form.enabled || !form.creditEnabled}
										onChange={e => setzeTyp('cap', typ, e.target.value)}
										aria-label={`${t('Cap per Junior Company (€, empty = none)')} ${typ}`}
									/>
								</label>
							</div>
						))}
					</div>

					<h4 className={styles.subTitle}>{t('Reminder')}</h4>
					<label className={own.feld}>
						{t(
							'Remind the teacher … days before the deadline of the last report if completions are missing (0 = no reminder)'
						)}
						<input
							type="text"
							inputMode="numeric"
							value={form.reminderDays}
							disabled={!form.enabled}
							onChange={e => setze('reminderDays', e.target.value)}
							aria-label={t('Reminder days')}
						/>
					</label>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							attention={geaendert}
							loading={speichert}
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

LearningCourses.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
